<template>
  <div class="container pap-form">
    <div v-if="showEmailChangeResult" class="mb-3">
      <AlertCard
        :type="resultType"
        :header="resultHeader"
        :message="resultMessage"
      />
    </div>

    <FormReauth
      v-if="showReauthForm"
      formTitle="Change Email: Verify Credentials"
      actionStatement="change your email address"
      @reauth-validated="onValidReauth($event)"
    />

    <Form
      v-if="showChangeEmailForm"
      key="changeEmail"
      :data="changeEmailForm"
      @form-validated="processChangeEmail($event)"
    >
      <template v-slot:topText>
        <div class="pap-form__text">
          <p>Please enter and submit your new email address.</p>
          <p>
            This will be your Pixapup login email <strong>and</strong> the
            address to which we'll send updates and notifications about your dog
            images, etc. (unless you have opted out of email notifications in
            Your Account >
            <router-link to="/profile">Profile/Preferences</router-link>).
          </p>
          <p>
            So, please be sure it's a <strong>good</strong> address you check
            often.
          </p>
          <p>Your current email: {{ authUser.email }}</p>
        </div>
      </template>
    </Form>
  </div>
</template>

<script>
import FormReauth from '@/components/form/FormReauth';
import Form from '@/components/form/Form';
import AlertCard from '@/components/notifications/AlertCard';

import { Login } from '@/models/Login';

import { mapGetters } from 'vuex';

export default {
  name: 'PageChangeEmail',
  components: {
    FormReauth,
    Form,
    AlertCard,
  },
  data: function () {
    return {
      showEmailChangeResult: false,
      showReauthForm: true,
      resultType: '',
      resultSuccessMsg: '',
      resultErrorMsg: '',
      changeEmailForm: this.changeEmailFormData(),
    };
  },

  computed: {
    ...mapGetters('auth', ['authUser']),

    showChangeEmailForm: function () {
      return !this.showEmailChangeResult && !this.showReauthForm;
    },

    resultHeader: function () {
      if (this.resultType === 'success') {
        return 'Success!';
      }
      if (this.resultType === 'error') {
        return 'There was a problem...';
      }
      return '';
    },
    resultMessage: function () {
      return this.resultErrorMsg ? this.resultErrorMsg : this.resultSuccessMsg;
    },
  },

  methods: {
    // Create and return the 'change email' form data obj for the corresponding Form component
    changeEmailFormData: function () {
      var formData = {
        formTitle: 'Change Your Email',
        headerIconClass: 'fas fa-at mr-2',
        // headerIconClass: 'far fa-envelope mr-2',
        orderedFields: ['email'],
        submitButtonText: 'Save',
        model: Login,
        fields: {
          email: {
            type: 'text',
            label: 'New Email',
            placeholder: 'New email address',
            maxLength: Login.getPropTruncateLength('email'),
            value: '',
            valid: null,
            errorMsg: '',
          },
        },
      };
      return formData;
    },

    onValidReauth: function () {
      this.showReauthForm = false;
    },

    processChangeEmail: async function (formData) {
      if (formData) {
        try {
          await this.$fapi.setUserEmail(formData.email);

          var userDocRef = this.$fapi.getDocRefById('users', this.authUser.uid);
          await userDocRef.update({
            email: formData.email,
            updatedAt: this.$fapi.firebase.firestore.FieldValue.serverTimestamp(),
          });

          // Set user's new email in state; it's a reactive property, so doing this will automatically update the
          // email shown in the welcome bar.
          this.$store.dispatch('auth/setAuthUserEmail', formData.email);

          this.resultType = 'success';
          this.resultSuccessMsg = `Your new email address was saved and you are now logged in as ${this.authUser.email}. 
          Use this new email whenever you log in.`;
        } catch (err) {
          // If user sat on this page for too long, the attempt to set their email would fail due to needing to reauthenticate, i.e.
          // error would be thrown: 'This operation is sensitive and requires recent authentication. Log in again before retrying this request.'
          // Check if that's the error message. If so, show the reauth form to start over.
          if (/recent authentication/.test(err.message)) {
            this.showReauthForm = true;
            return;
          }

          this.resultType = 'error';
          // If user enters an email that's in use by another account, this is the error message thrown:
          // The email address is already in use by another account.
          // For security reasons, don't want to show that. So, set error message to something generic.
          // this.resultErrorMsg = 'Your new email address was not saved: ' + err.message;
          this.resultErrorMsg =
            'The new email address you just entered was not saved. Your account is still set to your current email address: ' +
            this.authUser.email;
        }
        this.showEmailChangeResult = true;
      }
    },
  },
};
</script>

<style>
</style>